import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from '@mui/material';
import { LockOutlined as LockIcon } from '@mui/icons-material';
import api from '../services/api';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Estados para recuperación de contraseña
  const [openRecuperar, setOpenRecuperar] = useState(false);
  const [emailRecuperar, setEmailRecuperar] = useState('');
  const [loadingRecuperar, setLoadingRecuperar] = useState(false);
  const [mensajeRecuperar, setMensajeRecuperar] = useState('');
  const [tipoMensaje, setTipoMensaje] = useState('info');
  
  // Estados para restablecer contraseña (cuando viene con token en URL)
  const [searchParams] = useSearchParams();
  const tokenReset = searchParams.get('token');
  const [openReset, setOpenReset] = useState(false);
  const [passwordNueva, setPasswordNueva] = useState('');
  const [passwordConfirmar, setPasswordConfirmar] = useState('');
  const [loadingReset, setLoadingReset] = useState(false);
  
  // useEffect para abrir el dialog cuando hay token en URL
  useEffect(() => {
    if (tokenReset) {
      setOpenReset(true);
    }
  }, [tokenReset]);

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/login', { email, password });
      
      if (response.data.success) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        navigate('/');
        window.location.reload();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // SOLICITAR RECUPERACIÓN DE CONTRASEÑA
  // ============================================
  
  const handleSolicitarRecuperacion = async () => {
    if (!emailRecuperar) {
      setMensajeRecuperar('Por favor ingresa tu correo electrónico');
      setTipoMensaje('warning');
      return;
    }

    setLoadingRecuperar(true);
    setMensajeRecuperar('');

    try {
      const response = await api.post('/auth/recuperar-password', { email: emailRecuperar });
      
      setMensajeRecuperar(
        '✅ ' + response.data.message + '\n\n' +
        'Revisa tu bandeja de entrada y sigue las instrucciones del correo.'
      );
      setTipoMensaje('success');
      setEmailRecuperar('');
    } catch (error) {
      setMensajeRecuperar(
        '❌ Error: ' + (error.response?.data?.message || 'No se pudo enviar el correo')
      );
      setTipoMensaje('error');
    } finally {
      setLoadingRecuperar(false);
    }
  };

  // ============================================
  // RESTABLECER CONTRASEÑA CON TOKEN
  // ============================================
  
  const handleRestablecerPassword = async () => {
    if (passwordNueva.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    if (passwordNueva !== passwordConfirmar) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setLoadingReset(true);
    setError('');

    try {
      const response = await api.post('/auth/restablecer-password', {
        token: tokenReset,
        password: passwordNueva
      });
      
      setMensajeRecuperar('✅ ' + response.data.message + '\n\nYa puedes iniciar sesión con tu nueva contraseña.');
      setTipoMensaje('success');
      setOpenReset(false);
      setOpenRecuperar(true);
    } catch (error) {
      setError(error.response?.data?.message || 'Error al restablecer contraseña');
    } finally {
      setLoadingReset(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#1976d2',
      }}
    >
      <Paper
        elevation={6}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 400,
          textAlign: 'center',
        }}
      >
        <LockIcon sx={{ fontSize: 60, color: '#1976d2', mb: 2 }} />
        
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold', color: '#1976d2' }}>
          EJARAD-TIC
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          Sistema de Gestión Comercial
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Correo electrónico"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
            autoFocus
          />
          
          <TextField
            fullWidth
            label="Contraseña"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
          />

          <Box sx={{ textAlign: 'right', mt: 1 }}>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => {
                setOpenRecuperar(true);
                setMensajeRecuperar('');
                setEmailRecuperar('');
                setTipoMensaje('info');
              }}
              sx={{ textDecoration: 'none' }}
            >
              ¿Olvidaste tu contraseña?
            </Link>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            sx={{ mt: 3, mb: 2, backgroundColor: '#1976d2' }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Iniciar Sesión'}
          </Button>
        </form>
      </Paper>

      {/* ============================================
          DIALOG: SOLICITAR RECUPERACIÓN DE CONTRASEÑA
          ============================================ */}
      <Dialog 
        open={openRecuperar} 
        onClose={() => setOpenRecuperar(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ color: '#1976d2', fontWeight: 'bold' }}>
          Recuperar Contraseña
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.
          </Typography>
          
          <TextField
            fullWidth
            label="Correo electrónico"
            type="email"
            value={emailRecuperar}
            onChange={(e) => setEmailRecuperar(e.target.value)}
            margin="normal"
            required
            disabled={loadingRecuperar}
          />
          
          {mensajeRecuperar && (
            <Alert severity={tipoMensaje} sx={{ mt: 2, whiteSpace: 'pre-line' }}>
              {mensajeRecuperar}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setOpenRecuperar(false)} 
            color="inherit"
            disabled={loadingRecuperar}
          >
            Cerrar
          </Button>
          <Button 
            onClick={handleSolicitarRecuperacion} 
            variant="contained" 
            color="primary"
            disabled={loadingRecuperar || !emailRecuperar}
          >
            {loadingRecuperar ? <CircularProgress size={20} /> : 'Enviar enlace'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================
          DIALOG: RESTABLECER CONTRASEÑA (con token)
          ============================================ */}
      <Dialog 
        open={openReset} 
        onClose={() => setOpenReset(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ color: '#1976d2', fontWeight: 'bold' }}>
          Restablecer Contraseña
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Ingresa tu nueva contraseña.
          </Typography>
          
          <TextField
            fullWidth
            label="Nueva contraseña"
            type="password"
            value={passwordNueva}
            onChange={(e) => setPasswordNueva(e.target.value)}
            margin="normal"
            required
            helperText="Mínimo 6 caracteres"
          />
          
          <TextField
            fullWidth
            label="Confirmar contraseña"
            type="password"
            value={passwordConfirmar}
            onChange={(e) => setPasswordConfirmar(e.target.value)}
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setOpenReset(false)} 
            color="inherit"
            disabled={loadingReset}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleRestablecerPassword} 
            variant="contained" 
            color="primary"
            disabled={loadingReset || !passwordNueva || !passwordConfirmar}
          >
            {loadingReset ? <CircularProgress size={20} /> : 'Restablecer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Login;